import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mail,
  Settings,
  MessageCircle,
  Users,
  RefreshCw,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Send,
  Link,
  Webhook,
  Calendar,
  Palette,
  Image,
  Type,
  Upload,
  Video,
  FastForward,
  MonitorOff,
  Store,
  Shield,
  Download,
  FileSpreadsheet,
  Building2,
  Edit,
  Search,
  Phone,
  Smartphone,
  Key,
  History,
  BarChart3,
  FileText,
  UserPlus,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { branding, updateBranding, refreshBranding } = useBranding();
  const [activeTab, setActiveTab] = useState('smtp');
  
  // SMTP State
  const [smtpSettings, setSmtpSettings] = useState({
    provider: 'gmail',
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: 'Learning Academy',
    use_tls: true,
    send_to: '',
  });
  const [smtpLoading, setSMTPLoading] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  
  // WhatsApp State
  const [whatsappSettings, setWhatsappSettings] = useState({
    enabled: true,
    share_message_template: 'I just earned a new achievement on Learning Academy! 🎉 Check it out!',
  });
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  
  // SMS/OTP State
  const [smsSettings, setSmsSettings] = useState({
    enabled: false,
    otp_login_enabled: false,
    api_url: 'https://http.myvfirst.com/smpp/sendsms',
    auth_token: '',
    sender_id: '',
    sms_template: 'Your verification code is {OTP}. Valid for {VALIDITY} minutes.',
    otp_length: 4,
    otp_validity_minutes: 10,
    templates: {
      otp: {
        enabled: true,
        content: 'Your verification code to login in Parikrama Raymond is {OTP}. The OTP expires within {VALIDITY} minutes.',
        description: 'OTP Login Template',
        placeholders: ['{OTP}', '{VALIDITY}']
      },
      welcome: {
        enabled: true,
        content: 'Hi {USER},\nWelcome to Parikrama Raymond\'s learning platform. Please use below details to login into the application\nClick here: {LOGIN_URL}',
        description: 'Welcome SMS Template',
        placeholders: ['{USER}', '{LOGIN_URL}']
      }
    }
  });
  const [smsLoading, setSmsLoading] = useState(false);
  const [testingSms, setTestingSms] = useState(false);
  const [testingWelcomeSms, setTestingWelcomeSms] = useState(false);
  const [testMobileNumber, setTestMobileNumber] = useState('');
  const [testUserName, setTestUserName] = useState('');
  const [otpLogs, setOtpLogs] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [otpStats, setOtpStats] = useState(null);
  const [smsStats, setSmsStats] = useState(null);
  const [otpLogsLoading, setOtpLogsLoading] = useState(false);
  const [smsLogsLoading, setSmsLogsLoading] = useState(false);
  const [smsLogTypeFilter, setSmsLogTypeFilter] = useState('all');
  
  // HR Integration State
  const [hrIntegrations, setHrIntegrations] = useState([]);
  const [hrLoading, setHrLoading] = useState(false);
  const [syncingPlatform, setSyncingPlatform] = useState(null);
  const [newIntegrationDialog, setNewIntegrationDialog] = useState(false);
  const [newIntegration, setNewIntegration] = useState({
    platform: '',
    api_key: '',
    api_secret: '',
    base_url: '',
    enabled: true,
    sync_interval_hours: 24,
    webhook_secret: '',
  });

  // Video Controls State
  const [videoSettings, setVideoSettings] = useState({
    disable_video_forwarding: false,
    auto_pause_on_tab_switch: true,
  });
  const [videoLoading, setVideoLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setInitialLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [smtpRes, whatsappRes, hrRes, videoRes, smsRes] = await Promise.all([
        axios.get(`${API_URL}/settings/smtp`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/settings/whatsapp`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/settings/hr-integrations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/settings/video-controls`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/settings/sms`, { headers }).catch(() => ({ data: {} })),
      ]);
      
      if (smtpRes.data?.configured) {
        setSmtpSettings(prev => ({ ...prev, ...smtpRes.data }));
      }
      if (whatsappRes.data) {
        setWhatsappSettings(prev => ({ ...prev, ...whatsappRes.data }));
      }
      setHrIntegrations(hrRes.data || []);
      if (videoRes.data) {
        setVideoSettings(prev => ({ ...prev, ...videoRes.data }));
      }
      if (smsRes.data && smsRes.data.type === 'sms_config') {
        // Only update if we got valid data from backend
        const defaultTemplates = {
          otp: {
            enabled: true,
            content: 'Your verification code to login in Parikrama Raymond is {OTP}. The OTP expires within {VALIDITY} minutes.',
            description: 'OTP Login Template',
            placeholders: ['{OTP}', '{VALIDITY}']
          },
          welcome: {
            enabled: true,
            content: 'Hi {USER},\nWelcome to Parikrama Raymond\'s learning platform. Please use below details to login into the application\nClick here: {LOGIN_URL}',
            description: 'Welcome SMS Template',
            placeholders: ['{USER}', '{LOGIN_URL}']
          }
        };
        
        setSmsSettings({
          enabled: smsRes.data.enabled ?? false,
          otp_login_enabled: smsRes.data.otp_login_enabled ?? false,
          api_url: smsRes.data.api_url || 'https://http.myvfirst.com/smpp/sendsms',
          // Don't store masked token - use empty string to indicate "unchanged"
          auth_token: '',
          auth_token_set: !!(smsRes.data.auth_token && smsRes.data.auth_token.length > 5),
          sender_id: smsRes.data.sender_id || '',
          sms_template: smsRes.data.sms_template || 'Your verification code is {OTP}. Valid for {VALIDITY} minutes.',
          otp_length: smsRes.data.otp_length || 4,
          otp_validity_minutes: smsRes.data.otp_validity_minutes || 10,
          templates: smsRes.data.templates || defaultTemplates,
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // SMTP Handlers
  const handleSMTPProviderChange = (provider) => {
    const configs = {
      gmail: { host: 'smtp.gmail.com', port: 587 },
      office365: { host: 'smtp.office365.com', port: 587 },
      custom: { host: '', port: 587 },
    };
    setSmtpSettings(prev => ({ ...prev, provider, ...configs[provider] }));
  };

  const handleSaveSMTP = async () => {
    setSMTPLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Don't send send_to field when saving
      const { send_to, ...saveData } = smtpSettings;
      await axios.post(`${API_URL}/settings/smtp`, saveData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('SMTP settings saved successfully');
    } catch (error) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSMTPLoading(false);
    }
  };

  const handleTestSMTP = async () => {
    if (!smtpSettings.send_to) {
      toast.error('Please enter an email address to send test to');
      return;
    }
    setTestingSmtp(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/settings/smtp/test`, smtpSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to test SMTP settings');
    } finally {
      setTestingSmtp(false);
    }
  };

  // WhatsApp Handlers
  const handleSaveWhatsApp = async () => {
    setWhatsappLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/settings/whatsapp`, whatsappSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('WhatsApp settings saved successfully');
    } catch (error) {
      toast.error('Failed to save WhatsApp settings');
    } finally {
      setWhatsappLoading(false);
    }
  };

  // Video Controls Handlers
  const handleSaveVideoSettings = async () => {
    setVideoLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/settings/video-controls`, videoSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('settings.videoSettingsSaved', 'Video control settings saved successfully'));
    } catch (error) {
      toast.error(t('settings.videoSettingsFailed', 'Failed to save video control settings'));
    } finally {
      setVideoLoading(false);
    }
  };

  // SMS/OTP Handlers
  const handleSaveSmsSettings = async () => {
    setSmsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/settings/sms`, smsSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('SMS settings saved successfully');
    } catch (error) {
      toast.error('Failed to save SMS settings');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleTestSms = async () => {
    if (!testMobileNumber) {
      toast.error('Please enter a mobile number for testing');
      return;
    }
    setTestingSms(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/settings/sms/test`, { mobile_number: testMobileNumber }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Test OTP sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setTestingSms(false);
    }
  };

  const handleTestWelcomeSms = async () => {
    if (!testMobileNumber) {
      toast.error('Please enter a mobile number for testing');
      return;
    }
    setTestingWelcomeSms(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/settings/sms/test-welcome`, { 
        mobile_number: testMobileNumber,
        user_name: testUserName || 'Test User'
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Test welcome SMS sent successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test welcome SMS');
    } finally {
      setTestingWelcomeSms(false);
    }
  };

  const fetchSmsLogs = async () => {
    setSmsLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (smsLogTypeFilter !== 'all') {
        params.append('sms_type', smsLogTypeFilter);
      }
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/sms-logs?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/sms-stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setSmsLogs(logsRes.data?.logs || []);
      setSmsStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch SMS logs:', error);
    } finally {
      setSmsLogsLoading(false);
    }
  };

  const fetchOtpLogs = async () => {
    setOtpLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/otp-logs?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/otp-stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setOtpLogs(logsRes.data?.logs || []);
      setOtpStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch OTP logs:', error);
    } finally {
      setOtpLogsLoading(false);
    }
  };

  // HR Integration Handlers
  const handleAddIntegration = async () => {
    if (!newIntegration.platform || !newIntegration.api_key || !newIntegration.base_url) {
      toast.error('Please fill in all required fields');
      return;
    }
    setHrLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/settings/hr-integrations`, newIntegration, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('HR integration added successfully');
      setNewIntegrationDialog(false);
      setNewIntegration({
        platform: '',
        api_key: '',
        api_secret: '',
        base_url: '',
        enabled: true,
        sync_interval_hours: 24,
        webhook_secret: '',
      });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add HR integration');
    } finally {
      setHrLoading(false);
    }
  };

  const handleDeleteIntegration = async (platform) => {
    if (!window.confirm(`Are you sure you want to delete ${platform} integration?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/settings/hr-integrations/${platform}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Integration deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const handleSyncNow = async (platform) => {
    setSyncingPlatform(platform);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/settings/hr-integrations/${platform}/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Sync completed: ${response.data.result.employees_created} created, ${response.data.result.employees_updated} updated`);
      fetchSettings();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncingPlatform(null);
    }
  };

  const getPlatformName = (platform) => {
    const names = {
      zoho_people: 'Zoho People',
      people_strong: 'PeopleStrong',
      great_hr: 'GreatHR',
    };
    return names[platform] || platform;
  };

  if (user?.role !== 'admin') {
    return (
      <DashboardLayout>
        <Card className="p-12 text-center">
          <XCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Only administrators can access settings.</p>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="admin-settings">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Settings className="w-8 h-8 text-primary" />
            {t('settings.adminTitle', 'Admin Settings')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('settings.adminSubtitle', 'Configure branding, email, WhatsApp sharing, and HR integrations')}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="branding" data-testid="tab-branding">
              <Palette className="w-4 h-4 mr-2" />
              {t('settings.branding', 'Branding')}
            </TabsTrigger>
            <TabsTrigger value="video" data-testid="tab-video">
              <Video className="w-4 h-4 mr-2" />
              {t('settings.video', 'Video')}
            </TabsTrigger>
            <TabsTrigger value="smtp" data-testid="tab-smtp">
              <Mail className="w-4 h-4 mr-2" />
              {t('settings.email', 'Email')}
            </TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('settings.whatsapp', 'WhatsApp')}
            </TabsTrigger>
            <TabsTrigger value="sms" data-testid="tab-sms">
              <Phone className="w-4 h-4 mr-2" />
              {t('settings.smsOtp', 'SMS / OTP')}
            </TabsTrigger>
            <TabsTrigger value="hr" data-testid="tab-hr">
              <Users className="w-4 h-4 mr-2" />
              {t('settings.hr', 'HR')}
            </TabsTrigger>
          </TabsList>

          {/* Branding Settings */}
          <TabsContent value="branding" className="mt-6">
            <BrandingSettingsTab />
          </TabsContent>

          {/* Video Control Settings */}
          <TabsContent value="video" className="mt-6">
            <Card data-testid="video-settings-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  {t('settings.videoControlTitle', 'Video Player Controls')}
                </CardTitle>
                <CardDescription>
                  {t('settings.videoControlDescription', 'Configure video playback behavior for course content')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Disable Video Forwarding */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-full bg-orange-500/10">
                      <FastForward className="w-5 h-5 text-orange-500" />
                    </div>
                    <div>
                      <Label className="text-base font-medium">
                        {t('settings.disableForwarding', 'Disable Video Forwarding')}
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('settings.disableForwardingDesc', 'Prevent users from skipping or seeking ahead in videos. Users must watch the complete video to progress.')}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={videoSettings.disable_video_forwarding}
                    onCheckedChange={(checked) => setVideoSettings({ ...videoSettings, disable_video_forwarding: checked })}
                    data-testid="disable-forwarding-switch"
                  />
                </div>

                {/* Auto-Pause on Tab Switch */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-full bg-blue-500/10">
                      <MonitorOff className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <Label className="text-base font-medium">
                        {t('settings.autoPauseTabSwitch', 'Auto-Pause on Tab Switch')}
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('settings.autoPauseTabSwitchDesc', 'Automatically pause video playback when users switch to another browser tab or window.')}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={videoSettings.auto_pause_on_tab_switch}
                    onCheckedChange={(checked) => setVideoSettings({ ...videoSettings, auto_pause_on_tab_switch: checked })}
                    data-testid="auto-pause-switch"
                  />
                </div>

                <Separator />

                {/* Info Section */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <h4 className="font-medium mb-2">{t('settings.howItWorks', 'How it works')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start gap-2">
                      <FastForward className="w-4 h-4 mt-0.5 text-orange-500 flex-shrink-0" />
                      <span>{t('settings.forwardingInfo', 'When forwarding is disabled, the video progress bar and seek controls are locked. Users cannot skip ahead but can rewind to review content.')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <MonitorOff className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                      <span>{t('settings.autoPauseInfo', 'When auto-pause is enabled, videos will automatically pause if the user switches tabs or minimizes the browser. Playback resumes when they return.')}</span>
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={handleSaveVideoSettings}
                  disabled={videoLoading}
                  data-testid="save-video-settings-btn"
                >
                  {videoLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  {t('settings.saveVideoSettings', 'Save Video Settings')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMTP Settings */}
          <TabsContent value="smtp" className="mt-6">
            <Card data-testid="smtp-settings-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  SMTP Email Configuration
                </CardTitle>
                <CardDescription>
                  Configure email settings for welcome emails, notifications, and reminders
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Provider Selection */}
                <div className="space-y-2">
                  <Label>{t('settings.emailProvider', 'Email Provider')}</Label>
                  <Select
                    value={smtpSettings.provider}
                    onValueChange={handleSMTPProviderChange}
                  >
                    <SelectTrigger data-testid="smtp-provider">
                      <SelectValue placeholder={t('settings.selectProvider', 'Select provider')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gmail">Gmail</SelectItem>
                      <SelectItem value="office365">Office 365</SelectItem>
                      <SelectItem value="custom">{t('settings.smtpSettings', 'Custom SMTP')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-host">{t('settings.smtpHost', 'SMTP Host')}</Label>
                    <Input
                      id="smtp-host"
                      value={smtpSettings.host}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      data-testid="smtp-host"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-port">{t('settings.port', 'Port')}</Label>
                    <Input
                      id="smtp-port"
                      type="number"
                      value={smtpSettings.port}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) })}
                      placeholder="587"
                      data-testid="smtp-port"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-username">{t('settings.usernameEmail', 'Username / Email')}</Label>
                    <Input
                      id="smtp-username"
                      value={smtpSettings.username}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                      placeholder="your-email@gmail.com"
                      data-testid="smtp-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-password">{t('settings.passwordAppPassword', 'Password / App Password')}</Label>
                    <Input
                      id="smtp-password"
                      type="password"
                      value={smtpSettings.password}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                      placeholder="••••••••"
                      data-testid="smtp-password"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="from-email">{t('settings.fromEmail', 'From Email')}</Label>
                    <Input
                      id="from-email"
                      value={smtpSettings.from_email}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                      placeholder="noreply@company.com"
                      data-testid="smtp-from-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="from-name">{t('settings.fromName', 'From Name')}</Label>
                    <Input
                      id="from-name"
                      value={smtpSettings.from_name}
                      onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                      placeholder="Learning Academy"
                      data-testid="smtp-from-name"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={smtpSettings.use_tls}
                      onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, use_tls: checked })}
                      data-testid="smtp-use-tls"
                    />
                    <Label>{t('settings.useTLS', 'Use TLS')}</Label>
                  </div>
                </div>

                <Separator />

                {/* Test Email Section */}
                <div className="p-4 rounded-lg border bg-muted/30">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    {t('settings.sendTestEmail', 'Send Test Email')}
                  </h4>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="send-to">{t('settings.sendToEmail', 'Send To Email Address')}</Label>
                      <Input
                        id="send-to"
                        type="email"
                        value={smtpSettings.send_to}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, send_to: e.target.value })}
                        placeholder="recipient@example.com"
                        data-testid="smtp-send-to"
                      />
                      <p className="text-xs text-muted-foreground">
                        Enter the email address where you want to receive the test email
                      </p>
                    </div>
                    <Button
                      onClick={handleTestSMTP}
                      variant="outline"
                      disabled={testingSmtp || !smtpSettings.send_to}
                      className="w-full sm:w-auto"
                      data-testid="test-smtp-btn"
                    >
                      {testingSmtp ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('settings.sending', 'Sending Test...')}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {t('settings.sendTestEmailButton', 'Send Test Email')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <Separator />

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleSaveSMTP}
                    disabled={smtpLoading}
                    data-testid="save-smtp-btn"
                  >
                    {smtpLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    {t('settings.saveChanges', 'Save SMTP Settings')}
                  </Button>
                </div>

                {/* Email Types Info */}
                <div className="p-4 rounded-lg bg-muted/50">
                  <h4 className="font-medium mb-2">{t('settings.emailNotifications', 'Email notifications will be sent for:')}</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• {t('settings.welcomeEmails', 'Welcome emails when employees are synced')}</li>
                    <li>• {t('settings.courseAssignmentNotif', 'Course assignment notifications')}</li>
                    <li>• {t('settings.completionCerts', 'Course completion certificates')}</li>
                    <li>• {t('settings.complianceReminders', 'Compliance training reminders')}</li>
                    <li>• {t('settings.passwordResetRequests', 'Password reset requests')}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp Settings */}
          <TabsContent value="whatsapp" className="mt-6">
            <Card data-testid="whatsapp-settings-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  {t('settings.whatsappSharing', 'WhatsApp Sharing Configuration')}
                </CardTitle>
                <CardDescription>
                  {t('settings.whatsappDesc', 'Configure WhatsApp sharing for achievements and certificates')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">{t('settings.enableSharing', 'Enable WhatsApp Sharing')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t('settings.enableSharingDesc', 'Allow employees to share achievements on WhatsApp')}
                    </p>
                  </div>
                  <Switch
                    checked={whatsappSettings.enabled}
                    onCheckedChange={(checked) => setWhatsappSettings({ ...whatsappSettings, enabled: checked })}
                    data-testid="whatsapp-enabled"
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="share-template">{t('settings.shareTemplate', 'Share Message Template')}</Label>
                  <Textarea
                    id="share-template"
                    value={whatsappSettings.share_message_template}
                    onChange={(e) => setWhatsappSettings({ ...whatsappSettings, share_message_template: e.target.value })}
                    placeholder="I just earned a new achievement..."
                    rows={3}
                    data-testid="whatsapp-template"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('settings.shareTemplateDesc', 'This message will be pre-filled when employees share achievements')}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <h4 className="font-medium text-green-700 dark:text-green-400 mb-2">{t('common.preview', 'Preview')}</h4>
                  <p className="text-sm">{whatsappSettings.share_message_template}</p>
                  <p className="text-sm mt-2">🏆 [Badge Name]</p>
                  <p className="text-sm">[Badge Description]</p>
                </div>

                <Button
                  onClick={handleSaveWhatsApp}
                  disabled={whatsappLoading}
                  data-testid="save-whatsapp-btn"
                >
                  {whatsappLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  {t('settings.saveChanges', 'Save WhatsApp Settings')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SMS/OTP Settings */}
          <TabsContent value="sms" className="mt-6">
            {initialLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">{t('common.loading', 'Loading settings...')}</span>
              </div>
            ) : (
            <div className="space-y-6">
              {/* SMS Configuration Card */}
              <Card data-testid="sms-settings-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    {t('settings.smsOtpSettings', 'SMS Service Configuration')}
                  </CardTitle>
                  <CardDescription>
                    {t('settings.smsOtpDescription', 'Configure SMS gateway for OTP-based login authentication')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable SMS Service */}
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="space-y-0.5">
                      <Label className="text-base font-medium">{t('settings.enableSharing', 'Enable SMS Service')}</Label>
                      <p className="text-sm text-muted-foreground">
                        {t('settings.enableSmsDesc', 'Enable SMS gateway to send OTPs for authentication')}
                      </p>
                    </div>
                    <Switch
                      checked={smsSettings.enabled}
                      onCheckedChange={(checked) => setSmsSettings({ ...smsSettings, enabled: checked })}
                      data-testid="sms-enabled-switch"
                    />
                  </div>

                  {/* Enable OTP Login */}
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-blue-50 dark:bg-blue-950/30">
                    <div className="space-y-0.5">
                      <Label className="text-base font-medium flex items-center gap-2">
                        <Key className="w-4 h-4" />
                        Enable OTP-Based Login
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Allow users to login using mobile OTP instead of password
                      </p>
                    </div>
                    <Switch
                      checked={smsSettings.otp_login_enabled}
                      onCheckedChange={(checked) => setSmsSettings({ ...smsSettings, otp_login_enabled: checked })}
                      disabled={!smsSettings.enabled}
                      data-testid="otp-login-enabled-switch"
                    />
                  </div>

                  <Separator />

                  {/* API Configuration */}
                  <div className="space-y-4">
                    <h4 className="font-medium">API Configuration</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>API URL</Label>
                        <Input
                          value={smsSettings.api_url}
                          onChange={(e) => setSmsSettings({ ...smsSettings, api_url: e.target.value })}
                          placeholder="https://http.myvfirst.com/smpp/sendsms"
                          data-testid="sms-api-url"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Sender ID</Label>
                        <Input
                          value={smsSettings.sender_id}
                          onChange={(e) => setSmsSettings({ ...smsSettings, sender_id: e.target.value })}
                          placeholder="e.g., GYANOD"
                          data-testid="sms-sender-id"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Authorization Token</Label>
                      <Input
                        type="password"
                        value={smsSettings.auth_token}
                        onChange={(e) => setSmsSettings({ ...smsSettings, auth_token: e.target.value, auth_token_set: false })}
                        placeholder={smsSettings.auth_token_set ? "Token is set (enter new token to change)" : "Paste token from SMS provider"}
                        data-testid="sms-auth-token"
                      />
                      <p className="text-xs text-muted-foreground">
                        {smsSettings.auth_token_set && !smsSettings.auth_token 
                          ? "Token is already configured. Leave empty to keep current token, or paste new token to replace."
                          : "Paste the full authorization token from your SMS provider (without 'Bearer ' prefix)"
                        }
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* OTP Settings */}
                  <div className="space-y-4">
                    <h4 className="font-medium">OTP Configuration</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>OTP Length</Label>
                        <Select
                          value={String(smsSettings.otp_length)}
                          onValueChange={(value) => setSmsSettings({ ...smsSettings, otp_length: parseInt(value) })}
                        >
                          <SelectTrigger data-testid="otp-length-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="4">4 digits</SelectItem>
                            <SelectItem value="6">6 digits</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>OTP Validity (minutes)</Label>
                        <Select
                          value={String(smsSettings.otp_validity_minutes)}
                          onValueChange={(value) => setSmsSettings({ ...smsSettings, otp_validity_minutes: parseInt(value) })}
                        >
                          <SelectTrigger data-testid="otp-validity-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">5 minutes</SelectItem>
                            <SelectItem value="10">10 minutes</SelectItem>
                            <SelectItem value="15">15 minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* SMS Templates */}
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      SMS Templates
                    </h4>
                    
                    {/* OTP Template */}
                    <div className="p-4 rounded-lg border space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">OTP Login Template</Label>
                          <p className="text-xs text-muted-foreground">Used when user requests OTP for login</p>
                        </div>
                        <Switch
                          checked={smsSettings.templates?.otp?.enabled ?? true}
                          onCheckedChange={(checked) => setSmsSettings(prev => ({
                            ...prev,
                            templates: {
                              ...prev.templates,
                              otp: { ...prev.templates?.otp, enabled: checked }
                            }
                          }))}
                        />
                      </div>
                      <Textarea
                        value={smsSettings.templates?.otp?.content || ''}
                        onChange={(e) => setSmsSettings(prev => ({
                          ...prev,
                          templates: {
                            ...prev.templates,
                            otp: { ...prev.templates?.otp, content: e.target.value }
                          }
                        }))}
                        rows={3}
                        placeholder="Your verification code to login is {OTP}. The OTP expires within {VALIDITY} minutes."
                        data-testid="otp-template-content"
                      />
                      <p className="text-xs text-muted-foreground">
                        Placeholders: <code className="bg-muted px-1 rounded">{'{OTP}'}</code> - The OTP code, <code className="bg-muted px-1 rounded">{'{VALIDITY}'}</code> - Validity in minutes
                      </p>
                    </div>
                    
                    {/* Welcome Template */}
                    <div className="p-4 rounded-lg border space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">Welcome SMS Template</Label>
                          <p className="text-xs text-muted-foreground">Sent when new user is created or needs login info</p>
                        </div>
                        <Switch
                          checked={smsSettings.templates?.welcome?.enabled ?? true}
                          onCheckedChange={(checked) => setSmsSettings(prev => ({
                            ...prev,
                            templates: {
                              ...prev.templates,
                              welcome: { ...prev.templates?.welcome, enabled: checked }
                            }
                          }))}
                        />
                      </div>
                      <Textarea
                        value={smsSettings.templates?.welcome?.content || ''}
                        onChange={(e) => setSmsSettings(prev => ({
                          ...prev,
                          templates: {
                            ...prev.templates,
                            welcome: { ...prev.templates?.welcome, content: e.target.value }
                          }
                        }))}
                        rows={4}
                        placeholder="Hi {USER},\nWelcome to our learning platform.\nClick here: {LOGIN_URL}"
                        data-testid="welcome-template-content"
                      />
                      <p className="text-xs text-muted-foreground">
                        Placeholders: <code className="bg-muted px-1 rounded">{'{USER}'}</code> - User&apos;s name, <code className="bg-muted px-1 rounded">{'{LOGIN_URL}'}</code> - Login page URL
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Test SMS */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Test SMS Configuration</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Mobile Number</Label>
                        <Input
                          value={testMobileNumber}
                          onChange={(e) => setTestMobileNumber(e.target.value)}
                          placeholder="e.g., 919876543210"
                          data-testid="test-mobile-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>User Name (for Welcome SMS)</Label>
                        <Input
                          value={testUserName}
                          onChange={(e) => setTestUserName(e.target.value)}
                          placeholder="e.g., John Doe"
                          data-testid="test-user-name-input"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleTestSms}
                        disabled={testingSms || !smsSettings.enabled}
                        data-testid="test-otp-sms-btn"
                      >
                        {testingSms ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Test OTP
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleTestWelcomeSms}
                        disabled={testingWelcomeSms || !smsSettings.enabled}
                        data-testid="test-welcome-sms-btn"
                      >
                        {testingWelcomeSms ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        Send Test Welcome SMS
                      </Button>
                    </div>
                  </div>

                  <Button
                    onClick={handleSaveSmsSettings}
                    disabled={smsLoading}
                    className="w-full"
                    data-testid="save-sms-btn"
                  >
                    {smsLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save SMS Settings
                  </Button>
                </CardContent>
              </Card>

              {/* OTP Logs Card */}
              <Card data-testid="otp-logs-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <History className="w-5 h-5" />
                        OTP Usage Logs
                      </CardTitle>
                      <CardDescription>
                        Track all OTPs sent and their usage status
                      </CardDescription>
                    </div>
                    <Button variant="outline" onClick={fetchOtpLogs} disabled={otpLogsLoading}>
                      {otpLogsLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Stats */}
                  {otpStats && (
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-primary">{otpStats.total_sent}</div>
                        <div className="text-xs text-muted-foreground">Total Sent</div>
                      </div>
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-green-600">{otpStats.total_verified}</div>
                        <div className="text-xs text-muted-foreground">Verified</div>
                      </div>
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-blue-600">{otpStats.total_used_for_login}</div>
                        <div className="text-xs text-muted-foreground">Used for Login</div>
                      </div>
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold">{otpStats.success_rate}%</div>
                        <div className="text-xs text-muted-foreground">Success Rate</div>
                      </div>
                    </div>
                  )}

                  {/* Logs Table */}
                  <ScrollArea className="h-[300px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Mobile</TableHead>
                          <TableHead>Sent At</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Used for Login</TableHead>
                          <TableHead>Attempts</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {otpLogs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center text-muted-foreground">
                              No OTP logs yet. Click Refresh to load.
                            </TableCell>
                          </TableRow>
                        ) : (
                          otpLogs.map((log) => (
                            <TableRow key={log.id}>
                              <TableCell className="font-mono">
                                {log.mobile_number?.slice(0, 3)}****{log.mobile_number?.slice(-3)}
                              </TableCell>
                              <TableCell>
                                {new Date(log.created_at).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {log.sms_sent ? (
                                  log.verified ? (
                                    <Badge className="bg-green-100 text-green-800">Verified</Badge>
                                  ) : (
                                    <Badge variant="outline">Sent</Badge>
                                  )
                                ) : (
                                  <Badge variant="destructive">Failed</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {log.used_for_login ? (
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-muted-foreground" />
                                )}
                              </TableCell>
                              <TableCell>{log.attempts || 0}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* All SMS History Card */}
              <Card data-testid="sms-logs-card" className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Phone className="w-5 h-5" />
                        All SMS History
                      </CardTitle>
                      <CardDescription>
                        Complete history of all SMS messages sent (OTP and Welcome)
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Select
                        value={smsLogTypeFilter}
                        onValueChange={(value) => {
                          setSmsLogTypeFilter(value);
                          // Trigger refetch
                          setTimeout(() => fetchSmsLogs(), 100);
                        }}
                      >
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Filter by type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="otp">OTP Only</SelectItem>
                          <SelectItem value="welcome">Welcome Only</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" onClick={fetchSmsLogs} disabled={smsLogsLoading}>
                        {smsLogsLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Refresh
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* SMS Stats */}
                  {smsStats && (
                    <div className="grid grid-cols-5 gap-4 mb-6">
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-primary">{smsStats.total_sent + smsStats.total_failed}</div>
                        <div className="text-xs text-muted-foreground">Total SMS</div>
                      </div>
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-blue-600">{smsStats.total_otp}</div>
                        <div className="text-xs text-muted-foreground">OTP SMS</div>
                      </div>
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-purple-600">{smsStats.total_welcome}</div>
                        <div className="text-xs text-muted-foreground">Welcome SMS</div>
                      </div>
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold text-green-600">{smsStats.today_total}</div>
                        <div className="text-xs text-muted-foreground">Today&apos;s SMS</div>
                      </div>
                      <div className="p-4 rounded-lg border text-center">
                        <div className="text-2xl font-bold">{smsStats.success_rate}%</div>
                        <div className="text-xs text-muted-foreground">Success Rate</div>
                      </div>
                    </div>
                  )}

                  {/* SMS Logs Table */}
                  <ScrollArea className="h-[350px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Mobile</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead>Sent At</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Message Preview</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {smsLogs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground">
                              No SMS logs yet. Click Refresh to load.
                            </TableCell>
                          </TableRow>
                        ) : (
                          smsLogs.map((log) => (
                            <TableRow key={log.id}>
                              <TableCell>
                                {log.sms_type === 'otp' ? (
                                  <Badge variant="outline" className="bg-blue-50">OTP</Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-purple-50">Welcome</Badge>
                                )}
                              </TableCell>
                              <TableCell className="font-mono">
                                {log.mobile_number?.slice(0, 3)}****{log.mobile_number?.slice(-3)}
                              </TableCell>
                              <TableCell>
                                {log.user_name || '-'}
                              </TableCell>
                              <TableCell>
                                {new Date(log.sent_at).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {log.sms_sent ? (
                                  <Badge className="bg-green-100 text-green-800">Sent</Badge>
                                ) : (
                                  <Badge variant="destructive">Failed</Badge>
                                )}
                              </TableCell>
                              <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
                                {log.message_content?.slice(0, 50)}...
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>

          {/* HR Integration Settings */}
          <TabsContent value="hr" className="mt-6">
            <Card data-testid="hr-settings-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {t('settings.hrPlatformIntegrations', 'HR Platform Integrations')}
                    </CardTitle>
                    <CardDescription>
                      {t('settings.hrPlatformDesc', 'Connect with Zoho People, PeopleStrong, GreatHR to sync employees')}
                    </CardDescription>
                  </div>
                  <Dialog open={newIntegrationDialog} onOpenChange={setNewIntegrationDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-integration-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('settings.addIntegration', 'Add Integration')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('settings.addHrIntegration', 'Add HR Integration')}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <Label>{t('settings.platform', 'Platform')}</Label>
                          <Select
                            value={newIntegration.platform}
                            onValueChange={(value) => setNewIntegration({ ...newIntegration, platform: value })}
                          >
                            <SelectTrigger data-testid="hr-platform-select">
                              <SelectValue placeholder={t('settings.selectHrPlatform', 'Select HR platform')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="zoho_people">Zoho People</SelectItem>
                              <SelectItem value="people_strong">PeopleStrong</SelectItem>
                              <SelectItem value="great_hr">GreatHR</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>{t('settings.apiKey', 'API Key')}</Label>
                          <Input
                            value={newIntegration.api_key}
                            onChange={(e) => setNewIntegration({ ...newIntegration, api_key: e.target.value })}
                            placeholder={t('settings.enterApiKey', 'Enter API key')}
                            data-testid="hr-api-key"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>{t('settings.apiSecretOptional', 'API Secret (Optional)')}</Label>
                          <Input
                            type="password"
                            value={newIntegration.api_secret}
                            onChange={(e) => setNewIntegration({ ...newIntegration, api_secret: e.target.value })}
                            placeholder={t('settings.enterApiSecret', 'Enter API secret')}
                            data-testid="hr-api-secret"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>{t('settings.baseUrl', 'Base URL')}</Label>
                          <Input
                            value={newIntegration.base_url}
                            onChange={(e) => setNewIntegration({ ...newIntegration, base_url: e.target.value })}
                            placeholder="https://api.zoho.com/people/v1"
                            data-testid="hr-base-url"
                          />
                        </div>

                        <Separator />

                        <div className="space-y-4">
                          <h4 className="font-medium flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {t('settings.syncSettings', 'Sync Settings')}
                          </h4>
                          
                          <div className="space-y-2">
                            <Label>{t('settings.autoSyncInterval', 'Auto-Sync Interval (hours)')}</Label>
                            <Input
                              type="number"
                              value={newIntegration.sync_interval_hours}
                              onChange={(e) => setNewIntegration({ ...newIntegration, sync_interval_hours: parseInt(e.target.value) })}
                              placeholder="24"
                              data-testid="hr-sync-interval"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>{t('settings.webhookSecret', 'Webhook Secret (for real-time sync)')}</Label>
                            <Input
                              value={newIntegration.webhook_secret}
                              onChange={(e) => setNewIntegration({ ...newIntegration, webhook_secret: e.target.value })}
                              placeholder={t('settings.optionalWebhookSecret', 'Optional webhook secret')}
                              data-testid="hr-webhook-secret"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t('settings.webhookUrl', 'Webhook URL')}: {process.env.REACT_APP_BACKEND_URL}/api/settings/hr-integrations/{newIntegration.platform || '[platform]'}/webhook
                            </p>
                          </div>
                        </div>

                        <Button
                          className="w-full"
                          onClick={handleAddIntegration}
                          disabled={hrLoading}
                          data-testid="save-integration-btn"
                        >
                          {hrLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="w-4 h-4 mr-2" />
                          )}
                          {t('settings.addIntegration', 'Add Integration')}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {hrIntegrations.length > 0 ? (
                  <div className="space-y-4">
                    {hrIntegrations.map((integration) => (
                      <div
                        key={integration.platform}
                        className="flex items-center justify-between p-4 rounded-lg border"
                        data-testid={`integration-${integration.platform}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-3 rounded-full ${
                            integration.enabled ? 'bg-green-500/10' : 'bg-muted'
                          }`}>
                            <Users className={`w-5 h-5 ${
                              integration.enabled ? 'text-green-500' : 'text-muted-foreground'
                            }`} />
                          </div>
                          <div>
                            <h4 className="font-semibold">{getPlatformName(integration.platform)}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={integration.enabled ? 'default' : 'secondary'}>
                                {integration.enabled ? t('settings.active', 'Active') : t('settings.disabled', 'Disabled')}
                              </Badge>
                              <Badge variant="outline">
                                {integration.sync_status === 'completed' ? (
                                  <><CheckCircle2 className="w-3 h-3 mr-1" /> {t('settings.synced', 'Synced')}</>
                                ) : integration.sync_status === 'syncing' ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {t('settings.syncing', 'Syncing')}</>
                                ) : (
                                  <><Clock className="w-3 h-3 mr-1" /> {t('settings.neverSynced', 'Never Synced')}</>
                                )}
                              </Badge>
                            </div>
                            {integration.last_sync && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {t('settings.lastSync', 'Last sync')}: {new Date(integration.last_sync).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncNow(integration.platform)}
                            disabled={syncingPlatform === integration.platform}
                            data-testid={`sync-${integration.platform}`}
                          >
                            {syncingPlatform === integration.platform ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                            <span className="ml-2 hidden sm:inline">{t('settings.syncNow', 'Sync Now')}</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteIntegration(integration.platform)}
                            data-testid={`delete-${integration.platform}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">{t('settings.noHrIntegrations', 'No HR Integrations')}</h3>
                    <p className="text-muted-foreground mb-4">
                      {t('settings.connectHrPlatform', 'Connect your HR platform to automatically sync employees')}
                    </p>
                    <Button onClick={() => setNewIntegrationDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('settings.addFirstIntegration', 'Add First Integration')}
                    </Button>
                  </div>
                )}

                {hrIntegrations.length > 0 && (
                  <div className="mt-6 p-4 rounded-lg bg-muted/50">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Webhook className="w-4 h-4" />
                      {t('settings.syncOptions', 'Sync Options')}
                    </h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• <strong>{t('settings.manualSync', 'Manual Sync')}:</strong> {t('settings.manualSyncDesc', 'Click "Sync Now" to pull employees immediately')}</li>
                      <li>• <strong>{t('settings.scheduledSync', 'Scheduled Sync')}:</strong> {t('settings.scheduledSyncDesc', 'Automatic sync runs based on your interval setting')}</li>
                      <li>• <strong>{t('settings.realtimeWebhook', 'Real-time Webhook')}:</strong> {t('settings.realtimeWebhookDesc', 'Configure webhook in your HR platform for instant sync')}</li>
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

// Branding Settings Tab Component
const BrandingSettingsTab = () => {
  const { t } = useTranslation();
  const { branding, updateBranding, refreshBranding } = useBranding();
  const [brandingForm, setBrandingForm] = useState({
    app_name: '',
    slogan: '',
    logo_url: '',
    primary_color: '#2563EB',
    portal_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);

  // Helper to resolve logo URL for display
  const getLogoDisplayUrl = (url) => {
    if (!url) return '';
    // If it's already a full URL, return as-is
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
      return url;
    }
    // If it's a relative path, prepend the backend URL
    if (url.startsWith('/api/') || url.startsWith('/uploads/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  useEffect(() => {
    if (branding && !branding.loading) {
      setBrandingForm({
        app_name: branding.app_name || '',
        slogan: branding.slogan || '',
        logo_url: branding.logo_url || '',
        primary_color: branding.primary_color || '#2563EB',
        portal_url: branding.portal_url || '',
      });
    }
  }, [branding]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store relative URL path so it works on any deployment domain
      const logoUrl = response.data.url;  // e.g., /api/uploads/xyz.png
      setBrandingForm(prev => ({ ...prev, logo_url: logoUrl }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      const success = await updateBranding(brandingForm);
      if (success) {
        toast.success('Branding settings saved successfully');
        refreshBranding();
      } else {
        toast.error('Failed to save branding settings');
      }
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const predefinedColors = [
    { name: 'Red', value: '#DA2439' },
    { name: 'Blue', value: '#2563EB' },
    { name: 'Green', value: '#16A34A' },
    { name: 'Purple', value: '#7C3AED' },
    { name: 'Orange', value: '#EA580C' },
    { name: 'Teal', value: '#0D9488' },
    { name: 'Pink', value: '#DB2777' },
    { name: 'Indigo', value: '#4F46E5' },
  ];

  return (
    <Card data-testid="branding-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="w-5 h-5" />
          {t('settings.brandingCustomization', 'Branding & Customization')}
        </CardTitle>
        <CardDescription>
          {t('settings.brandingDesc', "Customize your platform's appearance with your own branding")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* App Name & Slogan */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="app-name">{t('settings.appName', 'Application Name')}</Label>
            <Input
              id="app-name"
              value={brandingForm.app_name}
              onChange={(e) => setBrandingForm(prev => ({ ...prev, app_name: e.target.value }))}
              placeholder="Gyanodaya"
              data-testid="branding-app-name"
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.appNameHint', 'Shown in emails and page titles')}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="slogan">{t('settings.sloganTagline', 'Slogan / Tagline')}</Label>
            <Input
              id="slogan"
              value={brandingForm.slogan}
              onChange={(e) => setBrandingForm(prev => ({ ...prev, slogan: e.target.value }))}
              placeholder="Rising of Knowledge"
              data-testid="branding-slogan"
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.sloganHint', 'Appears under your app name')}
            </p>
          </div>
        </div>

        {/* Portal URL for Emails */}
        <div className="space-y-2">
          <Label htmlFor="portal-url">{t('settings.portalUrl', 'Portal URL (for Email Links)')}</Label>
          <Input
            id="portal-url"
            value={brandingForm.portal_url}
            onChange={(e) => setBrandingForm(prev => ({ ...prev, portal_url: e.target.value }))}
            placeholder="https://learn.yourcompany.com"
            data-testid="branding-portal-url"
          />
          <p className="text-xs text-muted-foreground">
            {t('settings.portalUrlHint', 'The base URL used in email links (e.g., "Start Learning" button). Leave empty to use default.')}
          </p>
        </div>

        <Separator />

        {/* Logo Upload */}
        <div className="space-y-4">
          <Label className="text-base font-medium">{t('settings.sidebarLogo', 'Sidebar Logo')}</Label>
          <div className="flex items-center gap-6">
            <div 
              className="relative cursor-pointer group"
              onClick={() => logoInputRef.current?.click()}
            >
              <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/50 group-hover:border-primary transition-colors flex items-center justify-center overflow-hidden bg-muted">
                {brandingForm.logo_url ? (
                  <img src={getLogoDisplayUrl(brandingForm.logo_url)} alt="Logo" className="w-full h-full object-contain p-2" />
                ) : (
                  <Image className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                <Upload className="w-6 h-6 text-white" />
              </div>
            </div>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              ref={logoInputRef}
              onChange={handleLogoUpload}
            />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-2">
                {t('settings.logoUploadHint', 'Upload your company logo for the sidebar menu. Recommended: PNG or SVG, max 5MB')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('settings.uploading', 'Uploading...')}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {t('settings.uploadLogo', 'Upload Logo')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        <Separator />

        {/* Primary Color */}
        <div className="space-y-4">
          <Label className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            {t('settings.brandColor', 'Brand Color')}
          </Label>
          
          {/* Color Preview */}
          <div className="flex items-center gap-4">
            <div 
              className="w-16 h-16 rounded-lg border shadow-sm"
              style={{ backgroundColor: brandingForm.primary_color }}
            />
            <div>
              <p className="font-medium">{brandingForm.primary_color}</p>
              <p className="text-sm text-muted-foreground">{t('settings.currentBrandColor', 'Current brand color')}</p>
            </div>
          </div>

          {/* Predefined Colors */}
          <div className="space-y-2">
            <Label className="text-sm">{t('settings.quickSelect', 'Quick Select')}</Label>
            <div className="flex flex-wrap gap-2">
              {predefinedColors.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setBrandingForm(prev => ({ ...prev, primary_color: color.value }))}
                  className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                    brandingForm.primary_color === color.value ? 'border-foreground ring-2 ring-offset-2 ring-foreground' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {/* Custom Color Input */}
          <div className="flex items-center gap-4">
            <div className="space-y-2 flex-1">
              <Label htmlFor="primary-color">{t('settings.customHexColor', 'Custom Hex Color')}</Label>
              <div className="flex gap-2">
                <Input
                  id="primary-color"
                  type="text"
                  value={brandingForm.primary_color}
                  onChange={(e) => setBrandingForm(prev => ({ ...prev, primary_color: e.target.value }))}
                  placeholder="#2563EB"
                  className="font-mono"
                  data-testid="branding-primary-color"
                />
                <input
                  type="color"
                  value={brandingForm.primary_color}
                  onChange={(e) => setBrandingForm(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-12 h-10 rounded border cursor-pointer"
                />
              </div>
            </div>
          </div>
          
          <p className="text-xs text-muted-foreground">
            {t('settings.colorAppliedTo', 'This color will be applied to buttons, links, and accent elements')}
          </p>
        </div>

        <Separator />

        {/* Preview Section */}
        <div className="p-4 rounded-lg border bg-muted/30">
          <h4 className="font-medium mb-3">{t('settings.livePreview', 'Live Preview')}</h4>
          <div className="flex items-center gap-4 p-4 rounded-lg bg-background border">
            {brandingForm.logo_url ? (
              <img src={getLogoDisplayUrl(brandingForm.logo_url)} alt="Preview" className="h-10 w-auto" />
            ) : (
              <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                <Image className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <h3 className="font-bold" style={{ color: brandingForm.primary_color }}>
                {brandingForm.app_name || 'Gyanodaya'}
              </h3>
              <p className="text-xs text-muted-foreground">{brandingForm.slogan || 'Rising of Knowledge'}</p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button 
              size="sm"
              style={{ backgroundColor: brandingForm.primary_color, borderColor: brandingForm.primary_color }}
            >
              {t('settings.sampleButton', 'Sample Button')}
            </Button>
            <Button size="sm" variant="outline" style={{ borderColor: brandingForm.primary_color, color: brandingForm.primary_color }}>
              {t('settings.outlineButton', 'Outline Button')}
            </Button>
          </div>
          {brandingForm.portal_url && (
            <p className="mt-3 text-xs text-muted-foreground">
              {t('settings.emailLinksWillUse', 'Email links will use')}: <span className="font-mono">{brandingForm.portal_url}</span>
            </p>
          )}
        </div>

        <Button
          onClick={handleSaveBranding}
          disabled={saving}
          className="w-full sm:w-auto"
          data-testid="save-branding-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle2 className="w-4 h-4 mr-2" />
          )}
          {t('settings.saveBrandingSettings', 'Save Branding Settings')}
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminSettings;
